"use client";

import { SiApple, SiGoogle } from "@icons-pack/react-simple-icons";

import { useFormStatus } from "react-dom";

import { Spinner } from "@/components/spinner";
import { Button } from "@/components/ui/button";
import { OAUTH_PROVIDERS, type OAuthProvider } from "@/lib/auth/oauth-providers";

/** Icons live here rather than in the registry: OAUTH_PROVIDERS is imported
 *  by Server Actions, and these are client components. Keyed by the same
 *  union, so a new provider fails to compile until it has one. */
const ICONS: Record<OAuthProvider, React.ComponentType<{ className?: string }>> = {
  google: SiGoogle,
  apple: SiApple,
};

type Props = {
  provider: OAuthProvider;
  /** "Sign in" or "Sign up" — the provider name is appended from the
   *  registry so the two can't drift from the flow actually running.
   *  Apple's HIG allows exactly this phrasing ("Sign in with Apple" /
   *  "Sign up with Apple"); don't reword it to something else. */
  verb: string;
  /** External in-flight state OR-ed with the form's own useFormStatus —
   *  the shell path intercepts the submit (preventDefault), so the form
   *  action never dispatches and useFormStatus alone would leave the
   *  button enabled mid-flight (double-tap mints a second PKCE verifier
   *  that invalidates the first tab's code). */
  pending?: boolean;
};

export function OAuthButton({ provider, verb, pending: externalPending }: Props) {
  const { pending: formPending } = useFormStatus();
  const pending = externalPending || formPending;
  const Icon = ICONS[provider];
  return (
    <Button
      type="submit"
      variant="outline"
      className="h-10 w-full gap-2 px-4"
      disabled={pending}
    >
      {pending ? <Spinner /> : <Icon className="size-4" />}
      {`${verb} with ${OAUTH_PROVIDERS[provider].name}`}
    </Button>
  );
}
